import { useEffect, useState } from 'react';
import {
  listBatches,
  deleteBatch,
  listSales,
  listReturnReviewItems,
  exportSalesCsv,
  exportReturnReviewItemsCsv,
} from '../api/sales';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/format';
import { exportRowsToCsv } from '../utils/downloadTemplate';

export default function SalesDataManagement() {
  const { isAdmin, canViewGP } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState('');
  const [viewingId, setViewingId] = useState('');
  const [viewingIssuesId, setViewingIssuesId] = useState('');

  function refresh() {
    setLoading(true);
    listBatches()
      .then(setBatches)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  if (!isAdmin) {
    return (
      <div>
        <h1 className="text-xl font-semibold text-gray-800 mb-4">Sales Data Management</h1>
        <p className="text-sm text-gray-400">Only admins can view and manage imported sales data.</p>
      </div>
    );
  }

  async function handleDelete(batch) {
    const label = batch.type === 'Sales Return Deduction' ? 'return deductions' : 'sales transactions';
    const confirmed = window.confirm(
      `This will permanently delete ${batch.count} ${label} from this import (uploaded ${new Date(
        batch.uploadedAt
      ).toLocaleString()}). This cannot be undone. Continue?`
    );
    if (!confirmed) return;
    setDeletingId(batch.uploadBatchId);
    try {
      await deleteBatch(batch.uploadBatchId);
      if (viewingId === batch.uploadBatchId) setViewingId('');
      refresh();
    } finally {
      setDeletingId('');
    }
  }

  function handleExportBatches() {
    const columns = [
      { key: 'uploadedAtLabel', label: 'Uploaded' },
      { key: 'type', label: 'Type' },
      { key: 'monthsLabel', label: 'Months' },
      { key: 'channelsLabel', label: 'Channels' },
      { key: 'count', label: 'Records' },
      { key: 'qty', label: 'Qty' },
      ...(canViewGP ? [{ key: 'revenue', label: 'Revenue' }] : []),
    ];
    const rows = batches.map((b) => ({
      ...b,
      uploadedAtLabel: new Date(b.uploadedAt).toLocaleString(),
      monthsLabel: b.months.join(', '),
      channelsLabel: b.channels.join(', '),
    }));
    exportRowsToCsv('sales-import-batches.csv', columns, rows);
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Sales Data Management</h1>
        <button
          onClick={handleExportBatches}
          disabled={batches.length === 0}
          className="px-3 py-2 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
        >
          Export CSV
        </button>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Every Sales Upload and Sales Return Upload is grouped here by import batch. View a batch's raw
        rows before deciding, or delete a batch entirely — e.g. one uploaded by mistake or a wrong file.
        Deleting only affects that batch's transactions; Customers, Products, and Salesmen masters are
        never touched.
      </p>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : batches.length === 0 ? (
        <div className="text-gray-400 text-sm">No imports yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">Uploaded</th>
                <th className="px-4 py-2 font-medium">Type</th>
                <th className="px-4 py-2 font-medium">Months</th>
                <th className="px-4 py-2 font-medium">Channels</th>
                <th className="px-4 py-2 font-medium text-right">Records</th>
                <th className="px-4 py-2 font-medium text-right">Qty</th>
                {canViewGP && <th className="px-4 py-2 font-medium text-right">Revenue</th>}
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <BatchRow
                  key={b.uploadBatchId}
                  batch={b}
                  canViewGP={canViewGP}
                  expanded={viewingId === b.uploadBatchId}
                  onToggleView={() => setViewingId(viewingId === b.uploadBatchId ? '' : b.uploadBatchId)}
                  issuesExpanded={viewingIssuesId === b.uploadBatchId}
                  onToggleIssues={() => setViewingIssuesId(viewingIssuesId === b.uploadBatchId ? '' : b.uploadBatchId)}
                  onDelete={() => handleDelete(b)}
                  deleting={deletingId === b.uploadBatchId}
                />
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BatchRow({ batch, canViewGP, expanded, onToggleView, issuesExpanded, onToggleIssues, onDelete, deleting }) {
  const isReturnBatch = batch.type === 'Sales Return Deduction';
  return (
    <>
      <tr className="border-t border-gray-100">
        <td className="px-4 py-2 whitespace-nowrap">{new Date(batch.uploadedAt).toLocaleString()}</td>
        <td className="px-4 py-2 whitespace-nowrap">
          <span
            className={
              isReturnBatch
                ? 'inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium'
                : 'inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium'
            }
          >
            {batch.type}
          </span>
        </td>
        <td className="px-4 py-2">{batch.months.join(', ') || '—'}</td>
        <td className="px-4 py-2">{batch.channels.join(', ') || '—'}</td>
        <td className="px-4 py-2 text-right">{batch.count.toLocaleString()}</td>
        <td className="px-4 py-2 text-right">{Number(batch.qty).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
        {canViewGP && (
          <td className="px-4 py-2 text-right">{formatCurrency(batch.revenue)}</td>
        )}
        <td className="px-4 py-2 text-right whitespace-nowrap">
          <button onClick={onToggleView} className="text-blue-600 hover:text-blue-800 text-xs mr-3">
            {expanded ? 'Hide' : 'View'}
          </button>
          {isReturnBatch && (
            <button onClick={onToggleIssues} className="text-red-700 hover:text-red-900 text-xs mr-3">
              {issuesExpanded ? 'Hide Issues' : 'Issues'}
            </button>
          )}
          <button
            onClick={onDelete}
            disabled={deleting}
            className="text-red-500 hover:text-red-700 text-xs disabled:opacity-40"
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={canViewGP ? 8 : 7} className="p-0 border-t border-gray-100 bg-gray-50">
            <BatchDetail batchId={batch.uploadBatchId} canViewGP={canViewGP} />
          </td>
        </tr>
      )}
      {issuesExpanded && (
        <tr>
          <td colSpan={canViewGP ? 8 : 7} className="p-0 border-t border-gray-100 bg-red-50">
            <ReturnReviewDetail
              batchId={batch.uploadBatchId}
              matchedCount={batch.count}
              matchedViaInvoice={batch.matchedViaInvoice}
              matchedViaCustomerName={batch.matchedViaCustomerName}
              canViewGP={canViewGP}
            />
          </td>
        </tr>
      )}
    </>
  );
}

const PAGE_SIZE = 25;

function BatchDetail({ batchId, canViewGP }) {
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setLoading(true);
    listSales({ uploadBatchId: batchId, page, limit: PAGE_SIZE })
      .then(setData)
      .finally(() => setLoading(false));
  }, [batchId, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  async function handleExport() {
    setExporting(true);
    try {
      await exportSalesCsv({ uploadBatchId: batchId });
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="p-4">
      <div className="flex justify-end mb-2">
        <button
          onClick={handleExport}
          disabled={exporting || data.total === 0}
          className="px-3 py-1.5 text-xs rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>
      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : (
        <>
          <div className="overflow-auto max-h-96 border border-gray-200 rounded bg-white">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 text-gray-600 text-left sticky top-0">
                <tr>
                  <th className="px-3 py-1.5 font-medium">Date</th>
                  <th className="px-3 py-1.5 font-medium">Invoice</th>
                  <th className="px-3 py-1.5 font-medium">Customer</th>
                  <th className="px-3 py-1.5 font-medium">Product</th>
                  <th className="px-3 py-1.5 font-medium">Unit</th>
                  <th className="px-3 py-1.5 font-medium text-right">Qty</th>
                  {canViewGP && <th className="px-3 py-1.5 font-medium text-right">Revenue</th>}
                  <th className="px-3 py-1.5 font-medium">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={canViewGP ? 8 : 7} className="px-3 py-4 text-center text-gray-400">
                      No rows
                    </td>
                  </tr>
                ) : (
                  data.items.map((t) => (
                    <tr key={t._id} className="border-t border-gray-100">
                      <td className="px-3 py-1.5 whitespace-nowrap">{new Date(t.date).toLocaleDateString()}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{t.invoiceNumber}</td>
                      <td className="px-3 py-1.5">{t.customerName}</td>
                      <td className="px-3 py-1.5">
                        {t.productCode} {t.productName}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">{t.unit}</td>
                      <td className="px-3 py-1.5 text-right">{t.qty}</td>
                      {canViewGP && <td className="px-3 py-1.5 text-right">{formatCurrency(t.revenue)}</td>}
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {t.isReturn && <span className="text-amber-700">SR</span>}
                        {t.isReturnDeduction && <span className="text-red-600">Deduction</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
            <span>{data.total} rows</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
              >
                Previous
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const REVIEW_TYPE_LABELS = {
  unitMismatch: 'Unit Mismatch',
  noCustomerMatch: 'Customer Not Found',
  noSaleFound: 'No Matching Sale',
};

function SummaryStat({ label, value, accent = 'text-gray-800' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-500 mb-0.5">{label}</div>
      <div className={`text-lg font-semibold ${accent}`}>{value.toLocaleString()}</div>
    </div>
  );
}

const MATCHED_TABS = {
  matchedInvoice: { label: 'Matched — Invoice', matchedVia: 'invoice' },
  matchedCustomerName: { label: 'Matched — Customer Name', matchedVia: 'customerName' },
};

function ReturnReviewDetail({ batchId, matchedCount, matchedViaInvoice, matchedViaCustomerName, canViewGP }) {
  const [tab, setTab] = useState('matchedInvoice');
  const [page, setPage] = useState(1);
  const [reviewData, setReviewData] = useState({ items: [], total: 0, counts: {} });
  const [matchedData, setMatchedData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [tab]);

  // Fetch the issue-type counts once up front (independent of which tab is selected),
  // so the tab labels show correct numbers immediately instead of "0" until visited.
  useEffect(() => {
    listReturnReviewItems({ uploadBatchId: batchId, limit: 1 }).then((res) =>
      setReviewData((prev) => ({ ...prev, counts: res.counts }))
    );
  }, [batchId]);

  const isMatchedTab = tab in MATCHED_TABS;

  useEffect(() => {
    setLoading(true);
    if (isMatchedTab) {
      listSales({ uploadBatchId: batchId, matchedVia: MATCHED_TABS[tab].matchedVia, page, limit: PAGE_SIZE })
        .then(setMatchedData)
        .finally(() => setLoading(false));
    } else {
      listReturnReviewItems({ uploadBatchId: batchId, type: tab || undefined, page, limit: PAGE_SIZE })
        .then((res) => setReviewData((prev) => ({ ...res, counts: res.counts ?? prev.counts })))
        .finally(() => setLoading(false));
    }
  }, [batchId, tab, page]);

  const total = isMatchedTab ? matchedData.total : reviewData.total;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Which endpoint exports the current tab depends on what the rows ARE, not on how
  // they look: a Matched tab lists SalesTransaction rows (the deductions that were
  // actually applied), the issue tabs list ReturnReviewItem rows (lines nothing was
  // deducted for). Two different collections, so two different exports.
  async function handleExport() {
    setExporting(true);
    try {
      if (isMatchedTab) {
        await exportSalesCsv({ uploadBatchId: batchId, matchedVia: MATCHED_TABS[tab].matchedVia });
      } else {
        await exportReturnReviewItemsCsv({ uploadBatchId: batchId, type: tab });
      }
    } finally {
      setExporting(false);
    }
  }
  const totalIssues = Object.values(reviewData.counts).reduce((sum, n) => sum + n, 0);
  // Total Records = every return line the file was processed into: Matched + all three
  // issue categories (Unit Mismatch, Customer Not Found, No Matching Sale).
  const totalRecords = matchedCount + totalIssues;

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <SummaryStat label="Total Records" value={totalRecords} />
        <SummaryStat label="Matched — Invoice" value={matchedViaInvoice ?? 0} accent="text-green-700" />
        <SummaryStat label="Matched — Customer Name" value={matchedViaCustomerName ?? 0} accent="text-green-700" />
        <SummaryStat label="Matched — Total" value={matchedCount} accent="text-green-700" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        <SummaryStat label="Unit Mismatch" value={reviewData.counts.unitMismatch ?? 0} accent="text-red-700" />
        <SummaryStat label="Customer Not Found" value={reviewData.counts.noCustomerMatch ?? 0} accent="text-red-700" />
        <SummaryStat label="No Matching Sale" value={reviewData.counts.noSaleFound ?? 0} accent="text-red-700" />
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
        <button
          onClick={() => setTab('matchedInvoice')}
          className={`px-2 py-1 rounded border ${
            tab === 'matchedInvoice' ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-300 text-green-700'
          }`}
        >
          Matched — Invoice ({matchedViaInvoice ?? 0})
        </button>
        <button
          onClick={() => setTab('matchedCustomerName')}
          className={`px-2 py-1 rounded border ${
            tab === 'matchedCustomerName' ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-300 text-green-700'
          }`}
        >
          Matched — Customer Name ({matchedViaCustomerName ?? 0})
        </button>
        {Object.entries(REVIEW_TYPE_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-2 py-1 rounded border ${
              tab === key ? 'bg-red-600 text-white border-red-600' : 'bg-white border-gray-300 text-red-700'
            }`}
          >
            {label} ({reviewData.counts[key] ?? 0})
          </button>
        ))}
        <button
          onClick={handleExport}
          disabled={exporting || total === 0}
          className="ml-auto px-2 py-1 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {loading ? (
        <div className="text-gray-400 text-sm">Loading…</div>
      ) : isMatchedTab ? (
        <div className="overflow-auto max-h-96 border border-gray-200 rounded bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-600 text-left sticky top-0">
              <tr>
                <th className="px-3 py-1.5 font-medium">Date</th>
                <th className="px-3 py-1.5 font-medium">Invoice</th>
                <th className="px-3 py-1.5 font-medium">Customer</th>
                <th className="px-3 py-1.5 font-medium">Product</th>
                <th className="px-3 py-1.5 font-medium">Unit</th>
                <th className="px-3 py-1.5 font-medium text-right">Qty Deducted</th>
                {canViewGP && <th className="px-3 py-1.5 font-medium text-right">Value Deducted</th>}
                <th className="px-3 py-1.5 font-medium">Return Number</th>
              </tr>
            </thead>
            <tbody>
              {matchedData.items.length === 0 ? (
                <tr>
                  <td colSpan={canViewGP ? 8 : 7} className="px-3 py-4 text-center text-gray-400">
                    No matches
                  </td>
                </tr>
              ) : (
                matchedData.items.map((t) => (
                  <tr key={t._id} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 whitespace-nowrap">{new Date(t.date).toLocaleDateString()}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.invoiceNumber}</td>
                    <td className="px-3 py-1.5">{t.customerName}</td>
                    <td className="px-3 py-1.5">
                      {t.productCode} {t.productName}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.unit}</td>
                    <td className="px-3 py-1.5 text-right">{t.qty}</td>
                    {canViewGP && <td className="px-3 py-1.5 text-right">{formatCurrency(t.revenue)}</td>}
                    <td className="px-3 py-1.5 whitespace-nowrap">{t.returnNumber}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-auto max-h-96 border border-gray-200 rounded bg-white">
          <table className="w-full text-xs">
            <thead className="bg-gray-100 text-gray-600 text-left sticky top-0">
              <tr>
                <th className="px-3 py-1.5 font-medium">Type</th>
                <th className="px-3 py-1.5 font-medium">Customer</th>
                <th className="px-3 py-1.5 font-medium">Product</th>
                <th className="px-3 py-1.5 font-medium">Unit</th>
                <th className="px-3 py-1.5 font-medium">Sold Unit(s)</th>
                <th className="px-3 py-1.5 font-medium text-right">Return Qty</th>
                <th className="px-3 py-1.5 font-medium">Return Number</th>
                <th className="px-3 py-1.5 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {reviewData.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-gray-400">
                    No issues
                  </td>
                </tr>
              ) : (
                reviewData.items.map((r) => (
                  <tr key={r._id} className="border-t border-gray-100">
                    <td className="px-3 py-1.5 whitespace-nowrap">{REVIEW_TYPE_LABELS[r.type]}</td>
                    <td className="px-3 py-1.5">{r.customerName || r.rawCustomer}</td>
                    <td className="px-3 py-1.5">
                      {r.productCode} {r.productName}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.unit}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.soldUnits}</td>
                    <td className="px-3 py-1.5 text-right">{r.returnQty}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.returnNumber}</td>
                    <td className="px-3 py-1.5 whitespace-nowrap">
                      {r.type === 'noCustomerMatch' && r.rawCustomer && `From file: ${r.rawCustomer}`}
                      {r.type === 'noSaleFound' && r.checkedMonths && `Checked: ${r.checkedMonths}`}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
          <span>{total} rows</span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
